// @flow

import { useEffect, useMemo, useRef, useState } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { View } from 'reshaped';

import ContactFormContext from './ContactFormContext';
import { Section } from './Section';
import { BillingCheckbox } from './components/BillingCheckbox';
import { DelegateRadio } from './components/DelegateRadio';
import { EditingAlert } from './components/EditingAlert';
import { MSACheckbox } from './components/MSACheckbox';
import { ResellerBlock } from './components/ResellerBlock';
import { useDefaultFormValues } from './hooks/useDefaultFormValues';
import { useFormPage } from './hooks/useFormPage';
import { useOnSubmit } from './hooks/useOnSubmit';
import type { Contact, OrganizationFormData, Role } from './types';

type Props = {
  firstBuy?: boolean,
  contact?: Contact,
  onSuccess?: () => void,
  role?: Role,
  setCurrentRadioValue?: React.ReactSetStateFunction<?string>,
  /** @deprecated: To remove when cleaning FF_STUDIO_FIRST_BUY */
  studio?: boolean,
};

export function ContactForm({
  contact,
  onSuccess,
  firstBuy,
  role,
  setCurrentRadioValue,
  studio,
}: Props): React.MixedElement {
  const [disabled, setDisabled] = useState(false);
  const defaultValues = useDefaultFormValues(contact);
  const form = useForm({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
    disabled,
  });

  const [chooseProductOwner, setChooseProductOwner] = useState<boolean>(false);
  const [delegate, setDelegate] = useState<'no' | 'yes'>('no');

  const {
    formState: { dirtyFields },
    resetField,
    setValue,
  } = form;

  const type = form.watch('type');

  const canCreate = firstBuy || !contact?.id;
  const isTypeFieldDirty = canCreate && !!dirtyFields.type;
  const isEditingMode = (!!contact?.id && !isTypeFieldDirty) || !!studio;
  // immutable on edit
  useEffect(() => {
    if (isEditingMode) {
      if (dirtyFields.orgname && !!defaultValues.orgname) resetField('orgname');
      if (
        dirtyFields.firstname &&
        !!defaultValues.firstname &&
        type === 'individual'
      )
        resetField('firstname');
      if (
        dirtyFields.lastname &&
        !!defaultValues.lastname &&
        type === 'individual'
      )
        resetField('lastname');
    }
  }, [
    defaultValues.firstname,
    defaultValues.lastname,
    defaultValues.orgname,
    dirtyFields.firstname,
    dirtyFields.lastname,
    dirtyFields.orgname,
    isEditingMode,
    resetField,
    type,
  ]);

  // In the onboarding flow, the form acts as a hybrid.
  // It edits the pre-filled personal organization when the type is 'individual',
  // but creates a brand-new entity for any other type.
  const prevTypeRef = useRef<?string>(defaultValues.type);

  // Ensure personal user details don't leak into new company profiles
  // when switching types while preserving user-mutated state.
  useEffect(() => {
    const prevType = prevTypeRef.current;

    if (type !== undefined && prevType !== undefined && prevType !== type) {
      // dynamically get all fields except 'type'
      const formFields = (
        Object.keys(defaultValues) as any as Array<$Keys<OrganizationFormData>>
      ).filter(key => key !== 'type');

      formFields.forEach(field => {
        // never override what the user manually typed
        if (!dirtyFields[field]) {
          if (prevType === 'individual' && type !== 'individual') {
            setValue(field, '');
          } else if (prevType !== 'individual' && type === 'individual') {
            resetField(field);
          }
        }
      });

      prevTypeRef.current = type;
    }
  }, [type, dirtyFields, setValue, resetField, defaultValues]);

  const accountFormContextValue = useMemo(
    () => ({
      orgId: contact?.id,
      role,
    }),
    [contact?.id, role],
  );

  const isCustomer = role === 'CUSTOMER';

  const formPage = useFormPage({
    bu: contact?.bu_id,
    form,
    isEditingMode,
    isCustomer,
    contact,
    canCreate,
    studio,
  });

  const onSubmit = useOnSubmit({
    isEditingMode,
    dirtyFields,
    chooseProductOwner: studio ? false : chooseProductOwner,
    form,
    contact,
    firstBuy,
    role,
    setCurrentRadioValue,
    onSuccess,
    setDisabled,
    delegate,
    isCustomer,
    studio,
  });

  return (
    <ContactFormContext.Provider value={accountFormContextValue}>
      <View gap={7}>
        <FormProvider {...form}>
          {/* $FlowFixMe[prop-missing] warning about the onSubmit attributes' property */}
          <View
            as="form"
            attributes={{
              id: 'account-form',
              onSubmit: form.handleSubmit(onSubmit),
            }}
            paddingBottom={4}
          >
            {/* Sections */}
            <View gap={7}>
              {formPage.map((section, i) => (
                <View.Item key={section.title}>
                  {!firstBuy && !studio && isEditingMode && i === 0 && (
                    <EditingAlert />
                  )}
                  <Section section={section} />
                </View.Item>
              ))}
              {!isEditingMode && !isCustomer && <MSACheckbox />}

              {!studio &&
                (firstBuy ? (
                  <>
                    <BillingCheckbox
                      setChooseProductOwner={setChooseProductOwner}
                    />
                    <ResellerBlock createMode={canCreate} />
                  </>
                ) : (
                  role &&
                  {
                    CUSTOMER: null,
                    BILLING: <ResellerBlock createMode={canCreate} />,
                    PRODUCT_OWNER: !contact?.id && (
                      <DelegateRadio
                        delegate={delegate}
                        setDelegate={setDelegate}
                      />
                    ),
                  }[role]
                ))}
            </View>
          </View>
        </FormProvider>
      </View>
    </ContactFormContext.Provider>
  );
}
